import { Component, Input, Output, EventEmitter, OnInit } from '@angular/core';

export interface Step {
  id: string;
  title: string;
  description?: string;
  completed?: boolean;
  active?: boolean;
  disabled?: boolean;
  isValid?: boolean;
  hasValidationError?: boolean;
  errorMessage?: string;
  errorDetails?: string[];
}

@Component({
  selector: 'app-step-navigation',
  templateUrl: './step-navigation.component.html',
  styleUrls: ['./step-navigation.component.scss'],
})
export class StepNavigationComponent implements OnInit {
  @Input() steps: Step[] = [];
  @Input() currentStepIndex: number = 0;
  @Input() showStepNumbers: boolean = true;
  @Input() showStepDescriptions: boolean = true;
  @Input() allowBackNavigation: boolean = true;
  @Input() allowSkipSteps: boolean = false;
  @Input() backButtonText: string = 'Back';
  @Input() nextButtonText: string = 'Next';
  @Input() finishButtonText: string = 'Finish';
  @Input() isLastStep: boolean = false;
  @Input() loading: boolean = false;
  @Input() disableNext: boolean = false;
  @Input() showValidationErrors: boolean = false;

  @Output() stepChange = new EventEmitter<{ currentIndex: number; previousIndex: number }>();
  @Output() stepClick = new EventEmitter<{ step: Step; index: number }>();
  @Output() onBack = new EventEmitter<void>();
  @Output() onNext = new EventEmitter<void>();
  @Output() onFinish = new EventEmitter<void>();

  constructor() {
    // console.log('StepNavigationComponent: Constructor called');
  }

  ngOnInit(): void {
    // console.log('StepNavigationComponent: ngOnInit called');
    // console.log('StepNavigationComponent: Steps:', this.steps);
    // console.log('StepNavigationComponent: Current step index:', this.currentStepIndex);
    this.updateStepStates();
  }

  ngOnChanges(): void {
    // console.log('StepNavigationComponent: ngOnChanges called');
    // console.log('StepNavigationComponent: Steps updated:', this.steps);
    // console.log('StepNavigationComponent: Current step index updated:', this.currentStepIndex);
    this.updateStepStates();
  }

  private updateStepStates(): void {
    // console.log('StepNavigationComponent: Updating step states');
    this.steps.forEach((step, index) => {
      // Preserve existing validation error state
      const existingValidationError = step.hasValidationError;

      step.active = index === this.currentStepIndex;
      step.completed = index < this.currentStepIndex;
      step.disabled = !this.allowSkipSteps && index > this.currentStepIndex;

      // Always restore validation error state to ensure it persists across navigation
      step.hasValidationError = existingValidationError;
    });
    // console.log('StepNavigationComponent: Step states updated:', this.steps);
  }

  goToStep(index: number): void {
    // console.log('debugger: goToStep called with index:', index);

    // debugger;
    if (index < 0 || index >= this.steps.length) {
      // console.log('StepNavigationComponent: Invalid step index');
      return;
    }

    if (this.steps[index].disabled && !this.allowSkipSteps) {
      // console.log('StepNavigationComponent: Step is disabled and skip not allowed');
      return;
    }

    const previousIndex = this.currentStepIndex;
    this.currentStepIndex = index;
    this.updateStepStates();

    // console.log('StepNavigationComponent: Emitting stepChange event');
    this.stepChange.emit({ currentIndex: index, previousIndex });
    this.stepClick.emit({ step: this.steps[index], index });
  }

  goBack(): void {
    // console.log('StepNavigationComponent: goBack called');
    if (this.currentStepIndex > 0) {
      this.goToStep(this.currentStepIndex - 1);
      // console.log('StepNavigationComponent: Emitting onBack event');
      this.onBack.emit();
    }
  }

  goNext(): void {
    // console.log('StepNavigationComponent: goNext called');
    if (this.currentStepIndex < this.steps.length - 1) {
      this.goToStep(this.currentStepIndex + 1);
      // console.log('StepNavigationComponent: Emitting onNext event');
      this.onNext.emit();
    }
  }

  finish(): void {
    // console.log('StepNavigationComponent: finish called');
    // console.log('StepNavigationComponent: Emitting onFinish event');
    this.onFinish.emit();
  }

  canGoBack(): boolean {
    const canGo = this.allowBackNavigation && this.currentStepIndex > 0;
    // console.log('StepNavigationComponent: canGoBack:', canGo);
    return canGo;
  }

  canGoNext(): boolean {
    const canGo = this.currentStepIndex < this.steps.length - 1;
    // console.log('StepNavigationComponent: canGoNext:', canGo);
    return canGo;
  }

  isStepCompleted(step: Step): boolean {
    return step.completed || false;
  }

  isStepActive(step: Step): boolean {
    return step.active || false;
  }

  isStepDisabled(step: Step): boolean {
    return step.disabled || false;
  }

  isStepValid(step: Step): boolean {
    return step.isValid !== false; // Default to true if not specified
  }

  hasStepValidationError(step: Step): boolean {
    // console.log('StepNavigationComponent: hasStepValidationError called for step:', step, 'with value:', step.hasValidationError);
    return step.hasValidationError === true;
  }

  // Helper method to set error on a step
  setStepError(stepId: string, errorMessage: string, errorDetails?: string[]): void {
    const step = this.steps.find((s) => s.id === stepId);
    if (step) {
      step.hasValidationError = true;
      step.errorMessage = errorMessage;
      step.errorDetails = errorDetails || [];
    }
  }

  // Helper method to clear error from a step
  clearStepError(stepId: string): void {
    const step = this.steps.find((s) => s.id === stepId);
    if (step) {
      step.hasValidationError = false;
      step.errorMessage = undefined;
      step.errorDetails = undefined;
    }
  }

  // Helper method to get all steps with errors
  getStepsWithErrors(): Step[] {
    return this.steps.filter((step) => step.hasValidationError === true);
  }
}
